<template>
	<div class="optional-items">
		<!-- Package Margin specific form - only shows type dropdown and delta field -->
		<div v-if="isPackageMargin" class="package-margin-form">
			<FieldSet id="packageMarginSettings" :required="true">
				<p class="info-text mb-4">{{ $t('text.packageMarginTypeDescription') }}</p>

				<Field
					class="mb-4"
					typeName="AddedValue"
					fieldName="packageMarginType"
					v-model="modelValue.fields.packageMarginType"
					:title="$t('text.packageMarginType')"
					:infoText="$t('text.packageMarginTypeDescription')"
					:options="packageMarginTypeOptions"
				/>

				<Field
					typeName="AddedValue"
					fieldName="delta"
					v-model="modelValue.fields.delta"
					:title="$t('text.delta')"
					:infoText="$t('text.deltaInfoText')"
				/>
			</FieldSet>
		</div>

		<!-- Regular AV form -->
		<template v-else>
		<div @click="$refs.optionalSettingsDialog.open()"
			style="background: #fafafa; border-bottom: 1px solid #eee; border-top: 1px solid #eee; color: gray; font-size: smaller; margin-bottom: 20px; padding: 8px 24px; margin: -8px -24px 20px -24px; cursor: pointer;"
		>
			<v-icon>mdi-cog</v-icon>
			{{ $t('text.additionalSettingsCustomize') }}
		</div>

		<Optional id="purchaseType" v-model="optionalSettings" :autoShow="selectedAvType === AV_TYPES.OPTIONAL" :hidden="[AV_TYPES.FIXED, AV_TYPES.INCLUSIVE].includes(selectedAvType)">
			<Field typeName="AddedValue" fieldName="purchase" v-model="modelValue.fields.purchase" @change="onUpdatePurchaseType" required :title="$t('text.purchaseType')" :infoText="$t('text.purchaseTypeHelpText')"
				:disabled="[AV_TYPES.INCLUSIVE, AV_TYPES.FIXED].includes(selectedAvType)" />
		</Optional>

		<div>
			<Optional id="seasonalDiscounts" v-model="optionalSettings" :autoShow="autoShowSeasonalDiscounts" :hidden="[AV_TYPES.INCLUSIVE].includes(selectedAvType)" >
				<FieldSet id="seasonalDiscounts" class="pt-6">
					<AddButton @click="addSeasonalDiscount" buttonText="seasonalDiscount" />
					<EditableTable
						class="my-4"
						v-model="modelValue.fields.seasonalDiscounts.de"
						:columns="[
							'startDate',
							{ id: 'endDate', min: (item) => item.fields.startDate?.de },
							'reductionPercent',
						]"
						typeId="SeasonalDiscount"
						no-data-text="noSeasonalDiscountsSet"
						:hideFooter="false"
						v-model:offset="offset"
						v-model:limit="limit"
					>
						<template #deleteItem="{ item }">
							<v-icon class="deleteIcon" @click="(action) => handleOptionClick(item.sys.id, 'delete')">mdi-delete</v-icon>
						</template>
					</EditableTable>
				</FieldSet>
			</Optional>
			<Optional id="reductionSettings" v-model="optionalSettings" :autoShow="autoShowReductionSettings">
				<FieldSet id="reductionSettings">
					<Field typeName="AddedValue" fieldName="reductionPercent" v-model="modelValue.fields.reductionPercent" />
				</FieldSet>
			</Optional>
		</div>
		<Optional id="contingentSettings" v-model="optionalSettings" :autoShow="autoShowContingentSettings" :hidden="!isMysProduct && !(isGeneralPackage && isEventProduct) && [AV_TYPES.FIXED, AV_TYPES.INCLUSIVE, AV_TYPES.OV].includes(selectedAvType)" >
			<FieldSet id="contingentSettings">
				<div>
					<Field typeName="AddedValue" fieldName="maximumRedemptionCount" v-model="modelValue.fields.maximumRedemptionCount" />
					<Field v-if="(selectedAvType === AV_TYPES.OPTIONAL && modelValue.fields.purchase.de === 'wallet') || isMysProduct || (isGeneralPackage && isEventProduct)"
						class="mt-6" style="display: flex; gap: 10px;" typeName="AddedValue" fieldName="hasContingent" v-model="modelValue.fields.hasContingent"
						:showTitle="false" :disabled="!((selectedAvType === AV_TYPES.OPTIONAL && modelValue.fields.purchase.de === 'wallet') || isMysProduct || (isGeneralPackage && isEventProduct))"
						:options="[
							{ id: 'y', label: $t('text.perStay') },
							{ id: 'd', label: $t('text.perConsumptionDay') },
							{ id: 'generic', label: $t('text.generic') },
						]"
					/>
				</div>
			</FieldSet>
		</Optional>
		<Optional id="accessContingentSettings" v-model="optionalSettings" :autoShow="isMysProduct || (isGeneralPackage && isEventProduct) || model?.fields?.accessContingentNumber?.de || model?.fields?.accessContingentText?.de?.length > 0" >
			<FieldSet id="accessContingentSettings">
				<Field v-if="modelValue?.fields?.accessContingentNumber?.hasOwnProperty('de')" typeName="AddedValue" fieldName="accessContingentNumber" v-model="modelValue.fields.accessContingentNumber" />
				<Field v-else-if="modelValue?.fields?.accessContingentText?.hasOwnProperty('de')" typeName="AddedValue" fieldName="accessContingentText" v-model="modelValue.fields.accessContingentText" />
			</FieldSet>
		</Optional>
		<!-- TODO: this should actually be a field -->
		<Optional id="validitySettings" v-model="optionalSettings" :autoShow="true">
			<FieldSet id="validitySettings" >
				<Field typeName="AddedValue" fieldName="validityType" v-model="modelValue.fields.validityType" />
				<div style="display: flex; gap: 10px;" :class="{
					disabled: modelValue.fields.validityType?.de !== 'variable+',
				}">
					<Field typeName="AddedValue" fieldName="validDaysBefore" v-model="modelValue.fields.validDaysBefore" style="flex: 1;" :disabled="modelValue.fields.validityType?.de !== 'variable+'" />
					<Field typeName="AddedValue" fieldName="validDaysAfter" v-model="modelValue.fields.validDaysAfter" style="flex: 1;" :disabled="modelValue.fields.validityType?.de !== 'variable+'" />
				</div>
			</FieldSet>
		</Optional>

		<FieldSet id="additionalSettings" v-show="optionalSettings?.directPurchase || optionalSettings?.showPrice || optionalSettings?.marketingText || optionalSettings?.mandatory">
			<Optional id="directPurchase" v-model="optionalSettings" :autoShow="!!model?.fields?.directPurchase?.de" :hidden="[AV_TYPES.FIXED, AV_TYPES.OPTIONAL].includes(selectedAvType)" >
				<Field class="mb-4" typeName="AddedValue" fieldName="directPurchase" v-model="modelValue.fields.directPurchase"
					:disabled="[AV_TYPES.INCLUSIVE, AV_TYPES.FIXED, AV_TYPES.OPTIONAL].includes(selectedAvType)" />
			</Optional>

			<Optional id="showPrice" v-model="optionalSettings" :autoShow="!!model?.fields?.showPrice?.de">
				<Field class="mb-4" typeName="AddedValue" fieldName="showPrice" v-model="modelValue.fields.showPrice" :infoText="$t('text.showPriceInfoText')" />
			</Optional>

			<Optional id="mandatory" v-model="optionalSettings" :autoShow="!!model?.fields?.required?.de" :hidden="AV_TYPES.INCLUSIVE === selectedAvType">
				<Field class="mb-4" typeName="AddedValue" fieldName="required" v-model="modelValue.fields.required" :infoText="$t('text.mandatoryInfoText')" :title="$t('text.mandatory')" :disabled="[AV_TYPES.INCLUSIVE, AV_TYPES.FIXED, AV_TYPES.OPTIONAL].includes(selectedAvType)" />
			</Optional>

			<Optional id="marketingText" v-model="optionalSettings" :autoShow="!!model?.fields?.marketingText?.de?.length">
				<Field typeName="AddedValue" fieldName="marketingText" v-model="modelValue.fields.marketingText" :infoText="$t('text.marketingTextInfoText')" />
			</Optional>

			<Optional id="showContingent" v-model="optionalSettings" :autoShow="selectedAvType === AV_TYPES.OV && avType === 'event'" :hidden="!(selectedAvType === AV_TYPES.OV && avType === 'event')">
				<Field typeName="AddedValue" fieldName="showContingent" v-model="modelValue.fields.showContingent" :infoText="$t('text.showContingentInfoText')" :hide-switch-clear="true" />
			</Optional>
		</FieldSet>
		<div v-if="modelValue.fields.skipassProducts?.de?.length || avType === 'skipass'"
			style="margin-top: 10px; display: flex; gap: 5px; flex-direction: column; color: gray; font-size: smaller;"
		>
			<!--
			<div v-for="skipassProduct, s of modelValue.fields.skipassProducts?.de ?? []" :key="s">
				{{ skipassProduct }}
			</div>
			-->
			<SkuMultiSelect
				v-model="modelValue.fields.skipassProducts.de"
			/>
		</div>
		<DataDialog v-model="optionalSettings" :title="$t('text.customizeOptions')" ref="optionalSettingsDialog" :useWrap="true">
			<template #content="{ wrap }">
				<!-- TODO: the model is wrong here, we need a separate model per item.
							probably this whole dialog should actually move into the itemHead instead.
				-->
				<OptionalSettings v-model="wrap.model" @update:modelValue="wrap['model'] = $event" />
			</template>
		</DataDialog>
		</template>
	</div>
</template>

<script>
import DataDialog from '../../../../components/common/DataDialog.vue'
import Field from '../../../../components/fields/Field.vue'
import FieldSet from '../FieldSet.vue'
import Optional from '../Optional.vue'
import OptionalSettings from '../OptionalSettings.vue'
import Table from '../../../../components/common/tables/Table.vue'
import EditableTable from '../EditableTable.vue'
import AddButton from '../AddButton.vue'
import TagsField from '../../../../components/fields/TagsField.vue'
import SkuMultiSelect from '../SkuMultiSelect.vue'
import { AV_TYPES } from '@/constants'

export default {
	components: { DataDialog, Optional, FieldSet, Field, OptionalSettings, Table, EditableTable, AddButton, TagsField, SkuMultiSelect },
	props: {
		modelValue: Object,
		pricingType: String,
		selectedAvType: String,
		isRailawayPackage: Boolean,
		isGeneralPackage: Boolean,
		isPackageMargin: Boolean,
	},
	emits: ['update:modelValue'],
	data: () => ({
		optionalSettings: {},
		model: null,
		offset: 0,
		limit: 10,
	}),
	computed: {
		autoShowReductionSettings() {
			if (this.isRailawayPackage) return true
			else if (this.pricingType === 'fixed') return false
			else if (this.pricingType === 'sum') return [AV_TYPES.FIXED, AV_TYPES.OPTIONAL, AV_TYPES.INCLUSIVE].includes(this.selectedAvType)
			return this.model?.fields?.reductionPercent?.de > 0
		},
		autoShowSeasonalDiscounts() {
			if (this.isRailawayPackage) return true
			else if (this.pricingType === 'sum') return [AV_TYPES.FIXED, AV_TYPES.OPTIONAL].includes(this.selectedAvType)
			return this.model?.fields?.seasonalDiscounts?.de?.length > 0
		},
		autoShowContingentSettings() {
			if (this.isMysProduct) return true
			if (this.isGeneralPackage && this.isEventProduct) return true
			return !!(this.model?.fields?.maximumRedemptionCount?.de > 0 || this.model?.fields?.accessContingentText?.de || this.model?.fields?.accessContingentNumber?.de)
		},
		packageMarginTypeOptions() {
			return [
				{ key: 'transportation_margin', label: this.$t('text.transportationMargin') },
			]
		},
		avType() {
			const sku = this.modelValue.fields?.sku?.de?.[0]
			if (!sku?.startsWith) return
			// ATT: copied from PDS.upsertPermission
			let avType = null
			if (sku === 'TRANSPORTATION.TICKET' || sku.startsWith('BERGBAHN')) avType = 'bergbahn'
			else if (sku.startsWith('SKIPASS')) avType = 'skipass'
			else if (sku.startsWith('EVENT')) avType = 'event'
			//else throw `Could not determine added value type! ${sku}`
			return avType
		},
		accessContingentSettingsValue() {
			return this.optionalSettings?.accessContingentSettings
		},
		isMysProduct() {
			const sku = this.modelValue.fields?.sku?.de?.[0]
			return sku?.includes('EVENT.MYSERVICES.')
		},
		isEventProduct() {
			return this.avType === 'event'
		}
	},
	watch: {
		accessContingentSettingsValue() {
			if (this.accessContingentSettingsValue && !this.modelValue?.fields?.accessContingentNumber?.hasOwnProperty('de') && !this.modelValue?.fields?.accessContingentText?.hasOwnProperty('de')) {
				if (this.isMysProduct) {
					this.modelValue.fields.accessContingentText = { de: '1x' }
				}
				else {
					this.modelValue.fields.accessContingentText = { de: '' }
				}
			}
		}
	},
	methods: {
		onUpdatePurchaseType(value) {
			if (this.isMysProduct) {
				this.modelValue.fields.hasContingent.de = 'generic'
			} else {
				this.modelValue.fields.hasContingent.de = value?.de === 'wallet' ? 'y' : 'n'
			}
			this.modelValue.fields.maximumRedemptionCount.de = ['booking', 'wallet'].includes(value?.de) ? 1 : null
			this.optionalSettings.contingentSettings = value?.de === 'wallet' || this.isMysProduct
		},
		addSeasonalDiscount() {
			const seasonalDiscount = this.seasonalDiscountFactory()
			this.modelValue.fields.seasonalDiscounts.de.push(seasonalDiscount)
		},
		handleOptionClick(id, action) {
			if (action === 'delete') {
				const index = this.modelValue.fields.seasonalDiscounts.de.findIndex(item => item.sys.id === id)
				if (index >= 0) {
					this.modelValue.fields.seasonalDiscounts.de.splice(index, 1)
				}
			}
		},
		seasonalDiscountFactory() {
			return {
				sys: { id: '' },
				fields: {
					startDate: { de: null },
					endDate: { de: null },
					reductionPercent: { de: 0 },
				},
			}
		},
	},
	mounted() {
		this.model = JSON.parse(JSON.stringify(this.modelValue))

		// For MYS products, prefill access contingent to 1 if not already set
		if (this.isMysProduct) {
			this.modelValue.fields.hasContingent = { de: 'generic' }
			if (!this.modelValue?.fields?.accessContingentNumber?.hasOwnProperty('de') && !this.modelValue?.fields?.accessContingentText?.hasOwnProperty('de')) {
				this.modelValue.fields.accessContingentText = { de: '1x' }
			}
		}

		// For package margin AVs, ensure delta and packageMarginType fields are initialized
		if (this.isPackageMargin) {
			if (!this.modelValue?.fields?.delta?.hasOwnProperty('de')) {
				this.modelValue.fields.delta = { de: 1 }
			}
			if (!this.modelValue?.fields?.packageMarginType?.hasOwnProperty('de')) {
				this.modelValue.fields.packageMarginType = { de: 'transportation_margin' }
			}
		}
	},
	setup() {
		return { AV_TYPES }
	},
}
</script>

<style scoped>
.optional-items > * {
	/* TODO: this is breaking the left border and each optional item now has its own border. Is that fine? */
	padding-bottom: 20px;
}

.package-margin-form {
	padding: 16px 0;
}

.package-margin-form .info-text {
	font-size: 14px;
	color: #666;
	line-height: 1.5;
}
</style>